import React from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area } from 'recharts';
import { Contribution, Event } from '../../types';

interface ActivityReportChartProps {
  contributions: Contribution[];
  events: Event[];
  chartType?: 'line' | 'bar' | 'area';
  timeRange?: 'monthly' | 'weekly' | 'daily';
  height?: number;
  showBoth?: boolean;
}

export const ActivityReportChart: React.FC<ActivityReportChartProps> = ({
  contributions,
  events,
  chartType = 'line',
  timeRange = 'monthly',
  height = 300,
  showBoth = true
}) => {
  const processedData = React.useMemo(() => {
    const allActivities = [
      ...contributions.map(c => ({ ...c, activityType: 'contribution', date: c.date })),
      ...events.map(e => ({ ...e, activityType: 'event', date: e.date }))
    ].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    const grouped = allActivities.reduce((acc, activity) => {
      const date = new Date(activity.date);
      let key: string;

      if (timeRange === 'daily') {
        key = date.toISOString().split('T')[0];
      } else if (timeRange === 'weekly') {
        const weekStart = new Date(date);
        weekStart.setDate(date.getDate() - date.getDay());
        key = weekStart.toISOString().split('T')[0];
      } else {
        key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      }

      if (!acc[key]) {
        acc[key] = { period: key, contributions: 0, events: 0, totalActivities: 0 };
      }

      if (activity.activityType === 'contribution') {
        acc[key].contributions += 1;
      } else {
        acc[key].events += 1;
      }
      acc[key].totalActivities += 1;

      return acc;
    }, {} as Record<string, { period: string; contributions: number; events: number; totalActivities: number }>);

    return Object.values(grouped).map(item => ({
      ...item,
      periodLabel: timeRange === 'daily'
        ? new Date(item.period).toLocaleDateString()
        : timeRange === 'weekly'
          ? `Week of ${new Date(item.period).toLocaleDateString()}`
          : new Date(item.period + '-01').toLocaleDateString('en-US', { year: 'numeric', month: 'short' })
    }));
  }, [contributions, events, timeRange]);

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-semibold">{`${label}`}</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} style={{ color: entry.color }}>
              {`${entry.name}: ${entry.value}`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const renderChart = () => {
    const commonProps = {
      data: processedData,
      margin: { top: 5, right: 30, left: 20, bottom: 5 }
    };

    switch (chartType) {
      case 'area':
        return (
          <AreaChart {...commonProps}>
            <defs>
              <linearGradient id="colorContributions" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#8884d8" stopOpacity={0.8}/>
                <stop offset="95%" stopColor="#8884d8" stopOpacity={0}/>
              </linearGradient>
              <linearGradient id="colorEvents" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#82ca9d" stopOpacity={0.8}/>
                <stop offset="95%" stopColor="#82ca9d" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <XAxis dataKey="periodLabel" />
            <YAxis />
            <CartesianGrid strokeDasharray="3 3" />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            {showBoth ? (
              <>
                <Area type="monotone" dataKey="contributions" stackId="1" stroke="#8884d8" fillOpacity={1} fill="url(#colorContributions)" name="Contributions" />
                <Area type="monotone" dataKey="events" stackId="1" stroke="#82ca9d" fillOpacity={1} fill="url(#colorEvents)" name="Events" />
              </>
            ) : (
              <Area type="monotone" dataKey="totalActivities" stroke="#8884d8" fillOpacity={1} fill="url(#colorContributions)" name="Total Activities" />
            )}
          </AreaChart>
        );

      case 'bar':
        return (
          <BarChart {...commonProps}>
            <XAxis dataKey="periodLabel" />
            <YAxis />
            <CartesianGrid strokeDasharray="3 3" />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            {showBoth ? (
              <>
                <Bar dataKey="contributions" fill="#8884d8" name="Contributions" />
                <Bar dataKey="events" fill="#82ca9d" name="Events" />
              </>
            ) : (
              <Bar dataKey="totalActivities" fill="#8884d8" name="Total Activities" />
            )}
          </BarChart>
        );

      default: // line
        return (
          <LineChart {...commonProps}>
            <XAxis dataKey="periodLabel" />
            <YAxis />
            <CartesianGrid strokeDasharray="3 3" />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            {showBoth ? (
              <>
                <Line type="monotone" dataKey="contributions" stroke="#8884d8" strokeWidth={2} name="Contributions" />
                <Line type="monotone" dataKey="events" stroke="#82ca9d" strokeWidth={2} name="Events" />
              </>
            ) : (
              <Line type="monotone" dataKey="totalActivities" stroke="#8884d8" strokeWidth={2} name="Total Activities" />
            )}
          </LineChart>
        );
    }
  };

  const totals = React.useMemo(() => ({
    contributions: contributions.length,
    events: events.length,
    totalActivities: contributions.length + events.length
  }), [contributions, events]);

  return (
    <div className="w-full">
      <ResponsiveContainer width="100%" height={height}>
        {renderChart()}
      </ResponsiveContainer>

      <div className="mt-4 grid grid-cols-3 gap-4 text-sm">
        <div className="text-center">
          <p className="text-blue-600 font-semibold">{totals.contributions}</p>
          <p className="text-gray-600">Total Contributions</p>
        </div>
        <div className="text-center">
          <p className="text-green-600 font-semibold">{totals.events}</p>
          <p className="text-gray-600">Total Events</p>
        </div>
        <div className="text-center">
          <p className="text-purple-600 font-semibold">{totals.totalActivities}</p>
          <p className="text-gray-600">Total Activities</p>
        </div>
      </div>
    </div>
  );
};